/**
 * Bloque público de Diagnóstico NSYS para el portal de seguimiento.
 * Renderiza datos de Entrada y Salida si están disponibles.
 * No expone raw NSYS, solo lo que envía /seguimiento/verificar.
 */
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck, ShieldAlert, Battery, Cpu, FileText, CheckCircle2, XCircle,
} from 'lucide-react';

function StatRow({ label, value, ok }) {
  return (
    <div className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium ${ok === true ? 'text-emerald-700' : ok === false ? 'text-red-700' : ''}`}>
        {value ?? '—'}
      </span>
    </div>
  );
}

function Snapshot({ label, color, summary, tests, fechaIso }) {
  if (!summary) return null;
  return (
    <Card className={`p-4 border-l-4 ${color}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-sm flex items-center gap-1.5">
          <Cpu className="w-4 h-4" />
          Diagnóstico de {label}
        </h4>
        {fechaIso && <span className="text-[10px] text-muted-foreground">{new Date(fechaIso).toLocaleString('es-ES')}</span>}
      </div>
      <div className="space-y-1">
        <StatRow label="Modelo" value={summary.model} />
        <StatRow label="Grado" value={summary.grade} ok={!!summary.grade} />
        <StatRow label="Batería salud" value={summary.battery_health ? `${summary.battery_health}%` : null} ok={Number(summary.battery_health) >= 80} />
        <StatRow label="Ciclos" value={summary.battery_cycles} />
        <StatRow label="Capacidad" value={summary.battery_full_charge_capacity ? `${summary.battery_full_charge_capacity} mAh` : null} />
        <StatRow label="ESN" value={summary.esn || '—'} ok={summary.esn === 'CLEAN'} />
        <StatRow label="SIM Lock" value={summary.lock_status} ok={summary.lock_status === 'Unlocked'} />
        <StatRow label="FMIP/FRP" value={summary.fmip_frp || '0'} ok={!summary.fmip_frp || summary.fmip_frp === '0'} />
      </div>
      {summary.tests_total > 0 && (
        <div className="mt-3 flex items-center gap-3 text-xs p-2 bg-muted/30 rounded">
          <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /><strong>{summary.tests_passed}</strong> OK</span>
          <span className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-600" /><strong>{summary.tests_failed}</strong> fallidos</span>
          <span className="ml-auto text-muted-foreground">{summary.tests_total} tests</span>
        </div>
      )}
      {tests?.length > 0 && (
        <details className="mt-2">
          <summary className="text-xs cursor-pointer text-primary hover:underline">Ver detalle de todos los tests</summary>
          <table className="w-full text-[10px] mt-2 border-collapse">
            <thead className="bg-muted">
              <tr>
                <th className="p-1 text-left border">Test</th>
                <th className="p-1 text-left border">Grupo</th>
                <th className="p-1 text-center border w-10">Estado</th>
              </tr>
            </thead>
            <tbody>
              {tests.map((t, i) => (
                <tr key={i} className={t.value === 0 ? 'bg-red-50' : 'bg-emerald-50'}>
                  <td className="p-1 border">{t.item}</td>
                  <td className="p-1 border text-muted-foreground">{t.subgroup || t.group}</td>
                  <td className={`p-1 border text-center font-bold ${t.value === 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {t.value === 1 ? '✓' : '✗'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </Card>
  );
}

export default function NsysPublicBlock({ nsys, isAdmin = false }) {
  if (!nsys || (!nsys.entrada && !nsys.salida && !nsys.diagnostics_certificate)) {
    return null;
  }
  return (
    <Card className="p-4 space-y-3" data-testid="nsys-public-block">
      <div className="flex items-center gap-2 flex-wrap">
        <Cpu className="w-5 h-5 text-primary" />
        <h3 className="font-semibold">Diagnóstico técnico certificado (NSYS)</h3>
        {nsys.verified ? (
          <Badge className="bg-emerald-100 text-emerald-700">
            <ShieldCheck className="w-3 h-3 mr-1" />
            Verificado
          </Badge>
        ) : (
          <Badge variant="outline" className="text-amber-700 border-amber-300">
            <ShieldAlert className="w-3 h-3 mr-1" />
            En revisión
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Pruebas automatizadas realizadas con NSYS Diagnostics — herramienta certificada
        del sector. Se realizan pruebas a la <strong>entrada</strong> y a la <strong>salida</strong> del taller para garantizar la trazabilidad completa.
      </p>

      <div className="grid md:grid-cols-2 gap-3">
        <Snapshot
          label="ENTRADA"
          color="border-blue-500"
          summary={nsys.entrada}
          tests={nsys.tests_entrada}
          fechaIso={nsys.fetched_at_entrada}
        />
        <Snapshot
          label="SALIDA"
          color="border-emerald-500"
          summary={nsys.salida}
          tests={nsys.tests_salida}
          fechaIso={nsys.fetched_at_salida}
        />
      </div>

      {(nsys.diagnostics_certificate) && (
        <div className="flex flex-wrap gap-3 pt-2 border-t">
          {nsys.diagnostics_certificate && (
            <a
              href={nsys.diagnostics_certificate}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              data-testid="nsys-public-diag-cert"
            >
              <FileText className="w-4 h-4" />
              Descargar certificado oficial NSYS
            </a>
          )}
        </div>
      )}
    </Card>
  );
}
